import { FC, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import BeforeAfterSlider from './BeforeAfterSlider';
import { transformationService, TransformationItem } from '../services/transformationService';

const BeforeAfterProjects: FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [transformations, setTransformations] = useState<TransformationItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTransformations = async () => {
      try {
        const items = await transformationService.getTransformations();
        setTransformations(items.slice(0, 3)); // Pokazuj tylko 3 najnowsze
      } catch (error) {
        console.error('Error fetching transformations:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTransformations();
  }, []);

  if (loading) {
    return (
      <section id="transformations" className="py-16 bg-gray-100">
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-700"></div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="transformations" className="py-16 bg-gray-100">
      <div className="container mx-auto px-4 md:px-8">
        <motion.h2 
          className="text-3xl font-bold text-center mb-4"
          initial={{ opacity: 0, y: -20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: -20 }}
          transition={{ duration: 0.7 }}
          ref={ref}
        >
          Transformacje Przestrzeni
        </motion.h2>
        
        <motion.p 
          className="text-center text-gray-600 mb-12 max-w-2xl mx-auto"
          initial={{ opacity: 0, y: -20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: -20 }}
          transition={{ duration: 0.7, delay: 0.1 }}
        >
          Zobacz, jak przekształcamy przestrzenie naszych klientów, tworząc funkcjonalne 
          i estetyczne rozwiązania, które służą przez lata.
        </motion.p>

        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
          {transformations.map((transformation, index) => (
            <BeforeAfterSlider
              key={transformation.id}
              title={transformation.title}
              description={transformation.description}
              beforeImage={transformation.beforeImage}
              afterImage={transformation.afterImage}
              beforeAlt={`${transformation.title} - przed transformacją`}
              afterAlt={`${transformation.title} - po transformacji`}
              index={index}
            />
          ))}
        </div>

        {transformations.length === 0 && (
          <div className="text-center text-gray-600 py-16">
            <p>Brak transformacji do wyświetlenia.</p>
          </div>
        )}

        <motion.div 
          className="text-center mt-12"
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.7, delay: 0.4 }}
        >
          <Link 
            to="/galeria" 
            className="inline-block bg-blue-700 text-white px-8 py-3 rounded-lg hover:bg-blue-800 transition-colors duration-300"
          >
            Zobacz wszystkie projekty
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default BeforeAfterProjects;
